// src/components/LastPalmares/LastPalmares.jsx (ou ton chemin)
import React, { useEffect, useMemo, useState } from "react";
import { getAllPalmares } from "../../services/palmaresServices";
import SectionTitle from "../../components/ui/SectionTitle";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const FALLBACK = "/img/user_avatar.jpg"; // ou "/img/no-image.jpg"

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

export default function LastPalmares() {
  const [palmares, setPalmares] = useState([]);
  const [index, setIndex] = useState(0);
  const [animDir, setAnimDir] = useState("next"); // "next" | "prev"
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getAllPalmares();
        const actifs = (data || []).filter((p) => p.is_active);
        setPalmares(actifs);
        setIndex(0);
      } catch (e) {
        console.error("Erreur lors de la récupération des palmarès :", e);
        setPalmares([]);
      }
    })();
  }, []);

  const normalize = (s = "") =>
    String(s)
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase()
      .trim();

  // URL absolue pour photos adhérents
  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    if (
      /^https?:\/\//i.test(s) ||
      s.startsWith("data:") ||
      s.startsWith("blob:")
    )
      return s;
    if (!s.startsWith("/uploads")) return s;
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  const sorted = useMemo(() => {
    return [...palmares].sort((a, b) => {
      const da = new Date(a.date_palmares || 0).getTime();
      const db = new Date(b.date_palmares || 0).getTime();
      return db - da;
    });
  }, [palmares]);

  const filteredItems = useMemo(() => {
    const q = normalize(query);
    if (!q) return sorted;

    return sorted.filter((p) => {
      const nom = p?.adherent?.nom || "";
      const prenom = p?.adherent?.prenom || "";
      const event = p?.titre_palmares || "";
      const classement = p?.classement_palmares || "";

      const a = normalize(`${nom} ${prenom}`);
      const b = normalize(`${prenom} ${nom}`);
      const c = normalize(event);
      const d = normalize(classement);

      return a.includes(q) || b.includes(q) || c.includes(q) || d.includes(q);
    });
  }, [sorted, query]);

  // option perf Home : limiter après filtrage si tu veux
  const items = useMemo(() => filteredItems, [filteredItems]);

  useEffect(() => {
    setIndex(0);
  }, [query]);

  const total = items.length;
  const current = items[index] || null;

  useEffect(() => {
    if (!showAll) return;
    const onKey = (e) => {
      if (e.key === "Escape") setShowAll(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showAll]);

  const allForCurrent = useMemo(() => {
    if (!current) return [];

    const curId = current?.adherent?.id;

    // 1) match par id si possible
    let list = curId ? sorted.filter((p) => p?.adherent?.id === curId) : [];

    // 2) fallback si pas d'id (match nom+prenom)
    if (!list.length) {
      const key = normalize(
        `${current?.adherent?.nom || ""} ${current?.adherent?.prenom || ""}`,
      );
      list = sorted.filter((p) => {
        const k = normalize(
          `${p?.adherent?.nom || ""} ${p?.adherent?.prenom || ""}`,
        );
        return k === key;
      });
    }

    // tri récent -> ancien
    return [...list].sort((a, b) => {
      const da = new Date(a.date_palmares || 0).getTime();
      const db = new Date(b.date_palmares || 0).getTime();
      return db - da;
    });
  }, [current, sorted, query]); // query pas obligatoire, tu peux le retirer si tu veux

  const dateLabel = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const go = (nextIndex, direction) => {
    if (!total) return;
    setAnimDir(direction);
    setIndex((nextIndex + total) % total);
  };

  const prev = () => go(index - 1, "prev");
  const next = () => go(index + 1, "next");

  const cover = (item) => {
    const url = toAbsolute(item?.adherent?.photo_url);
    return url || FALLBACK;
  };

  const onImgError = (e) => {
    if (e?.currentTarget) e.currentTarget.src = FALLBACK;
  };

  return (
    <section
      className={cn(
        "py-12 sm:py-16",
        "bg-lightBackground dark:bg-darkBackground",
        "text-black dark:text-white",
      )}
      id="palmares"
    >
      {/* Animations locales */}
      <style>{`
        @keyframes pm-in-next { 
          from { opacity: 0; transform: translateX(16px); } 
          to   { opacity: 1; transform: translateX(0); } 
        }
        @keyframes pm-in-prev { 
          from { opacity: 0; transform: translateX(-16px); } 
          to   { opacity: 1; transform: translateX(0); } 
        }
        @media (prefers-reduced-motion: reduce) {
          .pm-anim { animation: none !important; transition: none !important; }
        }
      `}</style>

      <div className="mx-auto max-w-7xl px-4">
        <SectionTitle
          title="Derniers palmarès"
          subtitle={
            total
              ? "Les dernières performances du club"
              : "Aucun palmarès pour le moment"
          }
        />

        {!current ? (
          <div
            className={cn(
              "mt-10 rounded-3xl p-8 text-center",
              "border border-slate-200/70 dark:border-slate-800",
              "bg-white/70 dark:bg-slate-900/50 backdrop-blur shadow-sm",
            )}
          >
            <p className="text-slate-600 dark:text-slate-300">
              Aucun palmarès disponible.
            </p>
          </div>
        ) : (
          <div className="mt-10 grid gap-6 lg:grid-cols-12">
            {/* Spotlight */}
            <div
              className={cn(
                "relative overflow-hidden rounded-3xl",
                "border border-slate-200/70 dark:border-slate-800",
                "bg-white/80 dark:bg-slate-900/60 backdrop-blur",
                "shadow-sm",
                "lg:col-span-8",
              )}
            >
              {/* nav (desktop) */}
              {total > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prev}
                    className={cn(
                      "hidden md:grid absolute left-4 top-1/2 -translate-y-1/2 z-10",
                      "h-11 w-11 place-items-center rounded-full",
                      "bg-white/85 dark:bg-slate-950/60 backdrop-blur",
                      "border border-slate-200/70 dark:border-slate-800",
                      "text-slate-800 dark:text-slate-100 shadow hover:shadow-md transition",
                    )}
                    aria-label="Palmarès précédent"
                  >
                    <FiChevronLeft className="text-xl" />
                  </button>

                  <button
                    type="button"
                    onClick={next}
                    className={cn(
                      "hidden md:grid absolute right-4 top-1/2 -translate-y-1/2 z-10",
                      "h-11 w-11 place-items-center rounded-full",
                      "bg-white/85 dark:bg-slate-950/60 backdrop-blur",
                      "border border-slate-200/70 dark:border-slate-800",
                      "text-slate-800 dark:text-slate-100 shadow hover:shadow-md transition",
                    )}
                    aria-label="Palmarès suivant"
                  >
                    <FiChevronRight className="text-xl" />
                  </button>
                </>
              )}

              <div
                key={current.id}
                className={cn(
                  "pm-anim grid md:grid-cols-12",
                  animDir === "next"
                    ? "[animation:pm-in-next_.26s_ease-out]"
                    : "[animation:pm-in-prev_.26s_ease-out]",
                )}
              >
                {/* ✅ Photo plus grande */}
                <div className="md:col-span-5">
                  <img
                    src={cover(current)}
                    alt={`${current?.adherent?.nom || ""} ${current?.adherent?.prenom || ""}`}
                    className="h-72 md:h-full w-full object-cover"
                    onError={onImgError}
                    loading="lazy"
                    decoding="async"
                  />
                </div>

                {/* Infos */}
                <div className="md:col-span-7 p-5 sm:p-7">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-2xl font-extrabold text-slate-900 dark:text-slate-50 leading-tight">
                        <span className="uppercase">
                          {current?.adherent?.nom}
                        </span>{" "}
                        {current?.adherent?.prenom}
                      </div>

                      <div className="mt-2 inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800/60 px-3 py-1 text-xs font-semibold text-slate-700 dark:text-slate-200 border border-slate-200/70 dark:border-slate-800">
                        {dateLabel(current?.date_palmares)}
                      </div>
                    </div>

                    {/* Classement en badge (visuel, moderne) */}
                    <div className="shrink-0">
                      <div className="rounded-2xl px-4 py-2 bg-primary/10 text-primary border border-primary/25 font-extrabold text-lg">
                        {current?.classement_palmares}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-300 text-center">
                        classement
                      </div>
                    </div>
                  </div>

                  <div className="mt-5">
                    <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      Compétition
                    </div>
                    <div className="mt-1 text-base font-bold text-slate-900 dark:text-slate-50 leading-snug">
                      {current?.titre_palmares}
                    </div>

                    {allForCurrent.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => setShowAll(true)}
                        className={cn(
                          "mt-5 inline-flex items-center justify-center",
                          "rounded-full px-4 py-2 text-sm font-bold",
                          "bg-primary/10 text-primary border border-primary/25",
                          "hover:bg-primary/15 transition",
                        )}
                      >
                        Voir tous ses palmarès
                      </button>
                    ) : null}
                  </div>

                  {/* ✅ Controls mobile (flèches toujours visibles + dots scrollables sur 1 ligne) */}
                  {total > 1 && (
                    <div className="mt-6 md:hidden">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={prev}
                          className={cn(
                            "h-10 w-10 rounded-full grid place-items-center shrink-0",
                            "bg-white/85 dark:bg-slate-950/60 backdrop-blur",
                            "border border-slate-200/70 dark:border-slate-800",
                            "text-slate-800 dark:text-slate-100 shadow hover:shadow-md transition",
                          )}
                          aria-label="Précédent"
                        >
                          <FiChevronLeft />
                        </button>

                        {/* dots scrollables horizontalement (ne wrap plus) */}
                        <div
                          className={cn(
                            "flex-1 overflow-x-auto",
                            "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
                          )}
                        >
                          <div className="flex items-center gap-2 flex-nowrap w-max mx-auto px-1">
                            {items.map((it, i) => (
                              <button
                                key={it.id}
                                onClick={() =>
                                  go(i, i > index ? "next" : "prev")
                                }
                                className={cn(
                                  "h-2.5 w-2.5 rounded-full transition shrink-0",
                                  i === index
                                    ? "bg-primary"
                                    : "bg-slate-300 dark:bg-slate-700 hover:bg-slate-400 dark:hover:bg-slate-600",
                                )}
                                aria-label={`Aller au palmarès ${i + 1}`}
                              />
                            ))}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={next}
                          className={cn(
                            "h-10 w-10 rounded-full grid place-items-center shrink-0",
                            "bg-white/85 dark:bg-slate-950/60 backdrop-blur",
                            "border border-slate-200/70 dark:border-slate-800",
                            "text-slate-800 dark:text-slate-100 shadow hover:shadow-md transition",
                          )}
                          aria-label="Suivant"
                        >
                          <FiChevronRight />
                        </button>
                      </div>

                      <div className="mt-2 text-center text-xs text-slate-500 dark:text-slate-300">
                        {index + 1}/{total}
                      </div>
                    </div>
                  )}

                  {/* ✅ Dots desktop (peuvent rester centrés) */}
                  {total > 1 && (
                    <div className="mt-6 hidden md:flex items-center justify-center gap-2">
                      {items.map((it, i) => (
                        <button
                          key={it.id}
                          onClick={() => go(i, i > index ? "next" : "prev")}
                          className={cn(
                            "h-2.5 w-2.5 rounded-full transition",
                            i === index
                              ? "bg-primary"
                              : "bg-slate-300 dark:bg-slate-700 hover:bg-slate-400 dark:hover:bg-slate-600",
                          )}
                          aria-label={`Aller au palmarès ${i + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* accent bas */}
              <div
                aria-hidden
                className="h-1.5 w-full opacity-70"
                style={{
                  background:
                    "linear-gradient(to right, transparent, var(--tw-color-primary, #2563eb), transparent)",
                }}
              />
            </div>

            {/* Mini-liste cliquable (compacte, pas de scroll énorme) */}
            <aside className="lg:col-span-4">
              <div
                className={cn(
                  "rounded-3xl p-4",
                  "border border-slate-200/70 dark:border-slate-800",
                  "bg-white/70 dark:bg-slate-900/50 backdrop-blur shadow-sm",
                )}
              >
                <div className="text-sm font-extrabold text-slate-900 dark:text-slate-50">
                  À la une
                </div>
                <div className="mt-3 max-h-[520px] overflow-y-auto pr-1">
                  <div className="mt-3">
                    <label className="sr-only" htmlFor="palmares-search">
                      Rechercher un palmarès
                    </label>

                    <div className="relative">
                      <input
                        id="palmares-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Rechercher : nom, prénom, événement..."
                        className={cn(
                          "w-full rounded-2xl px-4 py-2.5 text-sm",
                          "bg-white/80 dark:bg-slate-950/60 backdrop-blur",
                          "border border-slate-200/70 dark:border-slate-800",
                          "text-slate-900 dark:text-slate-50 placeholder:text-slate-400",
                          "shadow-sm outline-none",
                          "focus:ring-2 focus:ring-primary/30",
                        )}
                      />
                      {query ? (
                        <button
                          type="button"
                          onClick={() => setQuery("")}
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full px-2 py-1 text-xs font-semibold text-slate-600 dark:text-slate-200 hover:text-primary transition"
                          aria-label="Effacer la recherche"
                        >
                          ✕
                        </button>
                      ) : null}
                    </div>

                    <div className="mt-2 text-xs text-slate-500 dark:text-slate-300 mb-4">
                      {items.length} palmarès trouvé(s)
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-3">
                    {items.map((it, i) => (
                      <button
                        key={it.id}
                        onClick={() => go(i, i > index ? "next" : "prev")}
                        className={cn(
                          "group text-left overflow-hidden rounded-2xl",
                          "border border-slate-200/70 dark:border-slate-800",
                          "bg-white dark:bg-slate-900/60",
                          "hover:shadow-md transition",
                          i === index && "ring-2 ring-primary/40",
                        )}
                        type="button"
                        aria-label={`Sélectionner ${it?.adherent?.nom || ""} ${it?.adherent?.prenom || ""}`}
                      >
                        <img
                          src={cover(it)}
                          alt=""
                          className="h-24 w-full object-cover"
                          onError={onImgError}
                          loading="lazy"
                        />
                        <div className="p-2">
                          <div className="text-xs font-extrabold text-slate-900 dark:text-slate-50 truncate">
                            {it?.adherent?.nom} {it?.adherent?.prenom}
                          </div>
                          <div className="mt-1 text-[11px] text-slate-600 dark:text-slate-300 line-clamp-2">
                            {it?.titre_palmares}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        )}
      </div>
      {showAll && current ? (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowAll(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className={cn(
              "relative w-full max-w-3xl overflow-hidden rounded-3xl",
              "bg-white/90 dark:bg-slate-950/80 backdrop-blur",
              "border border-slate-200/70 dark:border-slate-800",
              "shadow-2xl",
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 sm:p-6 border-b border-slate-200/70 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <img
                  src={cover(current)}
                  alt=""
                  className="h-12 w-12 rounded-full object-cover border border-slate-200/70 dark:border-slate-800"
                  onError={onImgError}
                />
                <div className="min-w-0">
                  <div className="text-lg font-extrabold text-slate-900 dark:text-slate-50">
                    {current?.adherent?.nom} {current?.adherent?.prenom}
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-300">
                    {allForCurrent.length} palmarès
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setShowAll(false)}
                  className={cn(
                    "ml-auto rounded-full px-3 py-1.5 text-sm font-bold",
                    "text-slate-700 dark:text-slate-200",
                    "border border-slate-200/70 dark:border-slate-800",
                    "hover:text-primary hover:border-primary/30 transition",
                  )}
                  aria-label="Fermer"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-5 sm:p-6">
              <div className="max-h-[65vh] overflow-y-auto pr-1 space-y-3">
                {allForCurrent.map((p) => (
                  <div
                    key={p.id}
                    className={cn(
                      "rounded-2xl p-4",
                      "border border-slate-200/70 dark:border-slate-800",
                      "bg-white/70 dark:bg-slate-900/50",
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-extrabold text-slate-900 dark:text-slate-50">
                          {p?.titre_palmares}
                        </div>
                        <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                          {dateLabel(p?.date_palmares)}
                        </div>
                      </div>

                      <div className="shrink-0 rounded-2xl px-3 py-1.5 bg-primary/10 text-primary border border-primary/25 font-extrabold">
                        {p?.classement_palmares}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowAll(false)}
                  className={cn(
                    "rounded-full px-4 py-2 text-sm font-bold",
                    "bg-slate-900 text-white hover:bg-slate-800 transition",
                    "dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200",
                  )}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
